/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// "Create Poll" composer modal — Figma 3466:9979 (default) / 3466:10106
// (filled). A centered modal card (599 wide) over a scrim:
//   - header "Create Poll" (#FFFFFF 18/700) + X close (icon #E8EAED),
//   - "Question or topic" label (#F5F6F8 12) + an input (r12, border
//     #222429, placeholder #6B7280) "What's the poll about",
//   - "Options" label + a bordered (r12 #222429) list of option rows
//     (trash #4B515C + text input + drag handle #8C929E), divided by
//     #222429 lines, ending in an "Add option" row,
//   - an "Allow multiple answers" row + a toggle (track #222429),
//   - a #222429 divider, then "Cancel" + a "Create" button (#537EFF).
//
// Pure presentation + local draft state; submission is delegated to the
// host via onSubmit({ state }) using the existing PollComposerState
// contract so the send wiring is unchanged. className-first throughout;
// the only inline styles are exact Figma hexes lucide consumes as JSX
// `color` props (not CSS) and the scrim color.

import type React from "react";
import { useCallback, useState } from "react";
import { Pressable, ScrollView, Switch, Text, TextInput, View } from "react-native";
import { GripVertical, Trash2, X } from "lucide-react-native";

import {
	POLL_MAX_OPTIONS,
	POLL_MIN_OPTIONS,
	type PollComposerState,
} from "./legacy/types";

export interface CreatePollModalProps {
	readonly onCancel: () => void;
	readonly onSubmit: (payload: { state: PollComposerState }) => void | Promise<void>;
}

interface DraftOption {
	readonly id: string;
	readonly text: string;
}

let optionSeq = 0;
const makeOption = (): DraftOption => ({ id: `opt-${optionSeq++}`, text: "" });

export const CreatePollModal: React.FC<CreatePollModalProps> = ({
	onCancel,
	onSubmit,
}) => {
	const [question, setQuestion] = useState("");
	const [options, setOptions] = useState<DraftOption[]>(() => [
		makeOption(),
		makeOption(),
	]);
	const [multiple, setMultiple] = useState(false);

	const filledOptions = options.filter((o) => o.text.trim().length > 0);
	const canSubmit =
		question.trim().length > 0 && filledOptions.length >= POLL_MIN_OPTIONS;

	const setOptionText = useCallback((id: string, text: string) => {
		setOptions((prev) => prev.map((o) => (o.id === id ? { ...o, text } : o)));
	}, []);

	const removeOption = useCallback((id: string) => {
		setOptions((prev) =>
			prev.length <= POLL_MIN_OPTIONS ? prev : prev.filter((o) => o.id !== id),
		);
	}, []);

	const addOption = useCallback(() => {
		setOptions((prev) =>
			prev.length >= POLL_MAX_OPTIONS ? prev : [...prev, makeOption()],
		);
	}, []);

	const handleCreate = useCallback(() => {
		if (!canSubmit) return;
		void onSubmit({
			state: {
				question: question.trim(),
				kind: multiple ? "multiple" : "single",
				anonymous: false,
				duration: "never",
				options: filledOptions.map((o) => ({ id: o.id, text: o.text.trim() })),
			},
		});
	}, [canSubmit, question, multiple, filledOptions, onSubmit]);

	return (
		<View
			className="flex-1 items-center justify-center px-4"
			style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
		>
			<View className="w-full max-w-[599px] rounded-2xl border border-[#222429] bg-[#15171C] p-6">
				{/* Header */}
				<View className="flex-row items-center justify-between">
					<Text className="text-[18px] font-bold text-white">Create Poll</Text>
					<Pressable
						accessibilityRole="button"
						accessibilityLabel="Close"
						onPress={onCancel}
						className="h-7 w-7 items-center justify-center rounded-xl active:opacity-70"
					>
						<X size={20} strokeWidth={2} color="#E8EAED" />
					</Pressable>
				</View>

				{/* Question */}
				<Text className="mt-5 mb-2 text-[12px] text-[#F5F6F8]">
					Question or topic
				</Text>
				<TextInput
					value={question}
					onChangeText={setQuestion}
					placeholder="What's the poll about"
					placeholderTextColor="#6B7280"
					className="h-11 rounded-xl border border-[#222429] bg-[#15171C] px-3 text-[14px] text-white"
				/>

				{/* Options */}
				<Text className="mt-4 mb-2 text-[12px] text-[#F5F6F8]">Options</Text>
				<View className="rounded-xl border border-[#222429] overflow-hidden">
					<ScrollView className="max-h-[200px]">
						{options.map((opt, index) => (
							<View
								key={opt.id}
								className={`flex-row items-center px-3 h-12 ${
									index > 0 ? "border-t border-[#222429]" : ""
								}`}
							>
								<Pressable
									accessibilityRole="button"
									accessibilityLabel={`Remove option ${index + 1}`}
									onPress={() => removeOption(opt.id)}
									disabled={options.length <= POLL_MIN_OPTIONS}
									className="mr-3 active:opacity-70"
								>
									<Trash2 size={18} strokeWidth={2} color="#4B515C" />
								</Pressable>
								<TextInput
									value={opt.text}
									onChangeText={(t) => setOptionText(opt.id, t)}
									placeholder={`Option ${index + 1}`}
									placeholderTextColor="#B5BAC3"
									className="flex-1 text-[14px] text-[#F5F6F8]"
								/>
								<GripVertical size={18} strokeWidth={2} color="#8C929E" />
							</View>
						))}
						{/* Add option */}
						{options.length < POLL_MAX_OPTIONS ? (
							<Pressable
								accessibilityRole="button"
								accessibilityLabel="Add option"
								onPress={addOption}
								className="flex-row items-center px-3 h-12 border-t border-[#222429] active:opacity-70"
							>
								<Text className="text-[14px] text-[#F5F6F8]">Add option</Text>
							</Pressable>
						) : null}
					</ScrollView>
				</View>

				{/* Allow multiple answers */}
				<View className="mt-4 flex-row items-center justify-between">
					<Text className="text-[14px] text-[#F5F6F8]">
						Allow multiple answers
					</Text>
					<Switch
						value={multiple}
						onValueChange={setMultiple}
						trackColor={{ false: "#222429", true: "#537EFF" }}
						thumbColor="#E8EAED"
					/>
				</View>

				{/* Footer */}
				<View className="mt-5 -mx-6 h-px bg-[#222429]" />
				<View className="mt-4 flex-row items-center justify-between">
					<Pressable
						accessibilityRole="button"
						accessibilityLabel="Cancel"
						onPress={onCancel}
						className="h-9 justify-center px-3 active:opacity-70"
					>
						<Text className="text-[14px] text-[#F5F6F8]">Cancel</Text>
					</Pressable>
					<Pressable
						accessibilityRole="button"
						accessibilityLabel="Create poll"
						onPress={handleCreate}
						disabled={!canSubmit}
						className={`h-9 items-center justify-center rounded-lg bg-[#537EFF] px-5 active:opacity-80 ${
							canSubmit ? "" : "opacity-40"
						}`}
					>
						<Text className="text-[14px] font-bold text-white">Create</Text>
					</Pressable>
				</View>
			</View>
		</View>
	);
};
